import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2, 
  ArrowLeft, 
  Save, 
  FileText,
  Package,
  Check,
  ChevronsUpDown,
  Pencil
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateDCPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Master data
  const [modes, setModes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  
  // Selected mode config
  const [selectedModeConfig, setSelectedModeConfig] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    mode: '',
    location_id: '',
    party_type: '',
    party_id: '',
    party_name: '',
    party_address: '',
    party_contact: '',
    returnable: false,
    expected_return_date: '',
    dispatch_date: new Date().toISOString().split('T')[0],
    auto_dispatch: true,
    remarks: ''
  });
  
  // DC Items
  const [dcItems, setDcItems] = useState([
    { item_id: '', item_name: '', quantity: '', unit: 'NOS', remarks: '', rejection_reason: '' }
  ]);
  
  // Track which item combobox is open (by index)
  const [openItemCombobox, setOpenItemCombobox] = useState(null);
  // Track which row is in "free text" mode
  const [freeTextMode, setFreeTextMode] = useState({});

  // Fetch master data
  const fetchMasterData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [modesRes, locRes, suppRes, clientRes, itemsRes] = await Promise.all([
        fetch(`${API_URL}/api/delivery-challans/modes`, { headers }),
        fetch(`${API_URL}/api/locations`, { headers }),
        fetch(`${API_URL}/api/suppliers`, { headers }),
        fetch(`${API_URL}/api/clients`, { headers }),
        fetch(`${API_URL}/api/items?limit=500`, { headers })
      ]);
      
      if (modesRes.ok) setModes(await modesRes.json());
      if (locRes.ok) setLocations(await locRes.json());
      if (suppRes.ok) setSuppliers(await suppRes.json());
      if (clientRes.ok) setClients(await clientRes.json());
      if (itemsRes.ok) {
        const itemsData = await itemsRes.json();
        setItems(itemsData.items || itemsData);
      }
    } catch (err) {
      console.error('Failed to fetch master data:', err);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  // Handle mode change
  const handleModeChange = (modeValue) => {
    const mode = modes.find(m => m.value === modeValue);
    setSelectedModeConfig(mode);
    
    setFormData(prev => ({
      ...prev,
      mode: modeValue,
      party_type: mode?.party_type || '',
      returnable: mode?.returnable_default || false,
      party_id: '',
      party_name: '',
      party_address: '',
      party_contact: ''
    }));
  };

  // Handle party selection
  const handlePartySelect = (partyId) => {
    const partyType = formData.party_type;
    let party = null;
    
    if (partyType === 'supplier' || partyType === 'jobber') {
      party = suppliers.find(s => s.id === partyId);
    } else if (partyType === 'customer') {
      party = clients.find(c => c.id === partyId);
    } else if (partyType === 'location') {
      party = locations.find(l => l.id === partyId);
    }
    
    if (party) {
      setFormData(prev => ({
        ...prev,
        party_id: partyId,
        party_name: party.name,
        party_address: party.address || '',
        party_contact: party.phone || party.contact_person || ''
      }));
    }
  };

  // Get party options based on type - sorted alphabetically
  const getPartyOptions = () => {
    const partyType = formData.party_type;
    let options = [];
    if (partyType === 'supplier' || partyType === 'jobber') {
      options = suppliers.map(s => ({ value: s.id, label: s.name }));
    } else if (partyType === 'customer') {
      options = clients.map(c => ({ value: c.id, label: c.name }));
    } else if (partyType === 'location') {
      options = locations.map(l => ({ value: l.id, label: l.name }));
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  };

  // Get user-friendly party type label
  const getPartyTypeLabel = () => {
    const partyType = formData.party_type;
    const labels = {
      'supplier': 'Supplier',
      'jobber': 'Jobber',
      'customer': 'Customer',
      'location': 'Location',
      'scrap_dealer': 'Scrap Dealer',
      'other': 'Party'
    };
    return labels[partyType] || 'Party';
  };

  // Handle item change
  const handleItemChange = (index, field, value) => {
    const newItems = [...dcItems];
    newItems[index][field] = value;
    
    // If item selected from dropdown, fill item_name
    if (field === 'item_id' && value) {
      const item = items.find(i => i.id === value);
      if (item) {
        newItems[index].item_name = item.name;
        newItems[index].unit = item.unit || 'NOS';
      }
    }
    
    setDcItems(newItems);
  };

  // Add item row
  const addItemRow = () => {
    setDcItems([...dcItems, { item_id: '', item_name: '', quantity: '', unit: 'NOS', remarks: '', rejection_reason: '' }]);
  };

  // Remove item row
  const removeItemRow = (index) => {
    if (dcItems.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    setDcItems(dcItems.filter((_, i) => i !== index));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.mode) {
      toast.error('Please select a DC mode');
      return false;
    }
    if (!formData.party_name) {
      toast.error('Please select or enter party name');
      return false;
    }
    
    const validItems = dcItems.filter(item => item.item_name && parseFloat(item.quantity) > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with quantity');
      return false;
    }
    
    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const validItems = dcItems
        .filter(item => item.item_name && parseFloat(item.quantity) > 0)
        .map(item => ({
          item_id: item.item_id || '',
          item_name: item.item_name,
          quantity: parseFloat(item.quantity),
          unit: item.unit || 'NOS',
          remarks: item.remarks || null,
          rejection_reason: item.rejection_reason || null
        }));
      
      const payload = {
        mode: formData.mode,
        location_id: formData.location_id || null,
        party_type: formData.party_type || null,
        party_id: formData.party_id || null,
        party_name: formData.party_name,
        party_address: formData.party_address || null,
        party_contact: formData.party_contact || null,
        items: validItems,
        returnable: formData.returnable,
        expected_return_date: formData.returnable && formData.expected_return_date ? formData.expected_return_date : null,
        dispatch_date: formData.dispatch_date || null,
        auto_dispatch: formData.auto_dispatch,
        remarks: formData.remarks || null
      };
      
      const res = await fetch(`${API_URL}/api/delivery-challans`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message || `DC ${data.dc_number} created successfully`);
        if (data.gate_pass_number) {
          toast.info(`Gate Pass ${data.gate_pass_number} auto-created`);
        }
        navigate('/store/dc');
      } else {
        toast.error(data.detail || 'Failed to create DC');
      }
    } catch (err) {
      console.error('Failed to create DC:', err);
      toast.error('Failed to create delivery challan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="create-dc-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/store/dc')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Delivery Challan</h1>
          <p className="text-sm text-gray-500">Universal document for non-sales outward movements</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Mode Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              DC Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {modes.filter(mode => mode.value !== 'inter_unit_transfer').map(mode => (
                <div
                  key={mode.value}
                  onClick={() => handleModeChange(mode.value)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.mode === mode.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`mode-${mode.value}`}
                >
                  <div className="font-medium">{mode.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{mode.description}</div>
                  <div className="flex gap-2 mt-2">
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{mode.prefix}</span>
                    {mode.returnable_default && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Returnable</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Party Details */}
        {formData.mode && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Party Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{getPartyTypeLabel()}</Label>
                  <SearchableSelect
                    options={getPartyOptions()}
                    value={formData.party_id}
                    onValueChange={(value) => {
                      if (!value) {
                        setFormData({ ...formData, party_id: '', party_name: '', party_address: '', party_contact: '' });
                      } else {
                        handlePartySelect(value);
                      }
                    }}
                    placeholder={`Select ${getPartyTypeLabel().toLowerCase()}...`}
                    searchPlaceholder={`Search ${getPartyTypeLabel().toLowerCase()}...`}
                    emptyText={`No ${getPartyTypeLabel().toLowerCase()} found`}
                    data-testid="party-select"
                  />
                </div>
                <div>
                  <Label>{getPartyTypeLabel()} Name *</Label>
                  <Input
                    value={formData.party_name}
                    onChange={(e) => setFormData({ ...formData, party_name: e.target.value.toUpperCase() })}
                    placeholder={`Enter ${getPartyTypeLabel().toLowerCase()} name`}
                    data-testid="party-name-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact</Label>
                  <Input
                    value={formData.party_contact}
                    onChange={(e) => setFormData({ ...formData, party_contact: e.target.value.toUpperCase() })}
                    placeholder="Phone / Contact person"
                  />
                </div>
              </div>
              
              <div>
                <Label>Address</Label>
                <Textarea
                  value={formData.party_address}
                  onChange={(e) => setFormData({ ...formData, party_address: e.target.value.toUpperCase() })}
                  placeholder="Party address"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Items */}
        {formData.mode && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Items
                </span>
                <Button type="button" variant="outline" size="sm" onClick={addItemRow}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[250px] w-[40%]">Item</TableHead>
                    <TableHead className="min-w-[100px] w-[15%]">Qty</TableHead>
                    <TableHead className="min-w-[80px] w-[10%]">Unit</TableHead>
                    <TableHead className="min-w-[150px] w-[30%]">Remarks / Reason</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dcItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="align-top">
                        <div className="space-y-1">
                          {/* Searchable Item Combobox */}
                          {!freeTextMode[index] ? (
                            <Popover 
                              open={openItemCombobox === index} 
                              onOpenChange={(open) => setOpenItemCombobox(open ? index : null)}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openItemCombobox === index}
                                  className="w-full justify-between font-normal h-10"
                                  data-testid={`item-select-${index}`}
                                >
                                  {item.item_id ? (
                                    <span className="truncate">
                                      {items.find(i => i.id === item.item_id)?.item_code 
                                        ? `${items.find(i => i.id === item.item_id)?.item_code} - ${items.find(i => i.id === item.item_id)?.name}`
                                        : items.find(i => i.id === item.item_id)?.name || 'Select item...'}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">Search items...</span>
                                  )}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[350px] p-0" align="start">
                                <Command>
                                  <CommandInput placeholder="Search by code or name..." />
                                  <CommandList>
                                    <CommandEmpty>No items found.</CommandEmpty>
                                    <CommandGroup>
                                      {/* Free Text Option */}
                                      <CommandItem
                                        value="__free_text__"
                                        onSelect={() => {
                                          handleItemChange(index, 'item_id', '');
                                          handleItemChange(index, 'item_name', '');
                                          setFreeTextMode({ ...freeTextMode, [index]: true });
                                          setOpenItemCombobox(null);
                                        }}
                                        className="text-blue-600"
                                      >
                                        <Pencil className="mr-2 h-4 w-4" />
                                        Free Text Entry
                                      </CommandItem>
                                      {/* Item List */}
                                      {items.map((i) => (
                                        <CommandItem
                                          key={i.id}
                                          value={`${i.item_code || ''} ${i.name}`}
                                          onSelect={() => {
                                            handleItemChange(index, 'item_id', i.id);
                                            setOpenItemCombobox(null);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              item.item_id === i.id ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          <span className="truncate">
                                            {i.item_code ? `${i.item_code} - ${i.name}` : i.name}
                                          </span>
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            /* Free Text Input Mode */
                            <div className="flex gap-1">
                              <Input
                                value={item.item_name}
                                onChange={(e) => handleItemChange(index, 'item_name', e.target.value.toUpperCase())}
                                placeholder="Type item name..."
                                className="flex-1"
                                autoFocus
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="px-2 text-blue-600 hover:text-blue-800"
                                onClick={() => {
                                  setFreeTextMode({ ...freeTextMode, [index]: false });
                                  handleItemChange(index, 'item_name', '');
                                }}
                                title="Switch to item search"
                              >
                                <ChevronsUpDown className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          {/* Show item name below when selected from master */}
                          {item.item_id && !freeTextMode[index] && (
                            <div className="text-xs text-gray-600 whitespace-normal break-words max-w-[250px]">
                              {item.item_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          min="0"
                          step="0.001"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          placeholder="00000.000"
                          className="text-right font-mono"
                          data-testid={`item-qty-${index}`}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value.toUpperCase())}
                          placeholder="Unit"
                          className="text-center"
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          value={formData.mode === 'purchase_return' ? item.rejection_reason : item.remarks}
                          onChange={(e) => handleItemChange(
                            index, 
                            formData.mode === 'purchase_return' ? 'rejection_reason' : 'remarks', 
                            e.target.value.toUpperCase()
                          )}
                          placeholder={formData.mode === 'purchase_return' ? 'Rejection reason' : 'Remarks'}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItemRow(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Options */}
        {formData.mode && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Options
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Location</Label>
                  <Select 
                    value={formData.location_id} 
                    onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location..." />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.code} - {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Dispatch Date</Label>
                  <Input
                    type="date"
                    value={formData.dispatch_date}
                    onChange={(e) => setFormData({ ...formData, dispatch_date: e.target.value })}
                  />
                </div>
                {selectedModeConfig?.returnable_default && (
                  <div>
                    <Label>Expected Return Date</Label>
                    <Input
                      type="date"
                      value={formData.expected_return_date}
                      onChange={(e) => setFormData({ ...formData, expected_return_date: e.target.value })}
                    />
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="auto_dispatch"
                    checked={formData.auto_dispatch}
                    onCheckedChange={(checked) => setFormData({ ...formData, auto_dispatch: checked })}
                  />
                  <label htmlFor="auto_dispatch" className="text-sm font-medium">
                    Auto-create Gate Pass (Ready for Dispatch)
                  </label>
                </div>
                
                {selectedModeConfig?.returnable_default && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="returnable"
                      checked={formData.returnable}
                      onCheckedChange={(checked) => setFormData({ ...formData, returnable: checked })}
                    />
                    <label htmlFor="returnable" className="text-sm font-medium">
                      Returnable
                    </label>
                  </div>
                )}
              </div>
              
              <div>
                <Label>Remarks</Label>
                <Textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value.toUpperCase() })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submit */}
        {formData.mode && (
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/store/dc')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="submit-dc-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Create DC
                </>
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
    </DashboardLayout>
  );
};

export default CreateDCPage;
